import { useEffect, useMemo, useRef, useState } from "react";
import { companyAutocomplete } from "@/lib/api/rfi";

type UseCompanyAutocompleteArgs = {
  query: string;
  enabled: boolean;
  minChars?: number;
  debounceMs?: number;
  maxSuggestions?: number;
};

type UseCompanyAutocompleteResult = {
  suggestions: string[];
  isLoading: boolean;
  error: string | null;
};

export function useCompanyAutocomplete({
  query,
  enabled,
  minChars = 3,
  debounceMs = 350,
  maxSuggestions = 8,
}: UseCompanyAutocompleteArgs): UseCompanyAutocompleteResult {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheRef = useRef<Map<string, string[]>>(new Map());
  const abortRef = useRef<AbortController | null>(null);
  const lastFetchedRef = useRef<string | null>(null);

  const normalizedQuery = useMemo(() => query.trim(), [query]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setError(null);
      setSuggestions([]);
      abortRef.current?.abort();
      abortRef.current = null;
      lastFetchedRef.current = null;
      return;
    }

    if (normalizedQuery.length < minChars) {
      setIsLoading(false);
      setError(null);
      setSuggestions([]);
      abortRef.current?.abort();
      abortRef.current = null;
      lastFetchedRef.current = null;
      return;
    }

    const cached = cacheRef.current.get(normalizedQuery);
    if (cached) {
      setIsLoading(false);
      setError(null);
      setSuggestions(cached.slice(0, maxSuggestions));
      return;
    }

    if (lastFetchedRef.current === normalizedQuery) return;

    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setIsLoading(true);
      setError(null);

      try {
        const res = await companyAutocomplete(normalizedQuery, ac.signal);
        const list = (res.suggestions ?? [])
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        cacheRef.current.set(normalizedQuery, list);
        lastFetchedRef.current = normalizedQuery;
        setSuggestions(list.slice(0, maxSuggestions));
      } catch (e) {
        // Ignore aborts; otherwise fail quietly (no toast) but hide suggestions.
        if ((e as any)?.name === "AbortError") return;
        setSuggestions([]);
        setError(e instanceof Error ? e.message : "Autocomplete failed");
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => window.clearTimeout(t);
  }, [debounceMs, enabled, maxSuggestions, minChars, normalizedQuery]);

  return { suggestions, isLoading, error };
}


